/**
 * FloorPlanAnalysisWizard - Multi-step wizard for floor plan analysis
 *
 * Flow:
 * 1. Upload: Drag-drop file upload
 * 2. Analyzing: Show progress while AI processes
 * 3. New Spaces: Review AI-detected spaces not in project
 * 4. Missing Spaces: Review existing spaces not found in plan (if any)
 * 5. Size Changes: Review spaces with different sizes (if any)
 * 6. Windows: Review detected windows (if any)
 * 7. Confirm: Summary of all changes before applying
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  ArrowLeft,
  Upload,
  X,
  Check,
  Warning,
  Plus,
  Trash,
  ArrowsLeftRight,
  FileImage,
  SpinnerGap,
  Square,
} from '@phosphor-icons/react';
import { useFloorPlanAnalysis } from '../../hooks/useFloorPlanAnalysis';
import FloorPlanEditor from './Editor/FloorPlanEditor';
import { pdfToImageUrl } from './Editor/pdfToImage';
import { useData } from '../../hooks/useData';
import { TABLES } from '../../constants';
import modalStyles from '../../styles/modals.module.css';
import styles from './FloorPlanAnalysisWizard.module.css';

// Wizard steps
const STEPS = {
  UPLOAD: 'upload',
  ANALYZING: 'analyzing',
  EDITOR: 'editor',
  NEW_SPACES: 'new_spaces',
  MISSING_SPACES: 'missing_spaces',
  SIZE_CHANGES: 'size_changes',
  WINDOWS: 'windows',
  CONFIRM: 'confirm',
};

// Step titles in Hebrew
const STEP_TITLES = {
  [STEPS.UPLOAD]: 'העלאת תוכנית קומה',
  [STEPS.ANALYZING]: 'מנתח את התוכנית...',
  [STEPS.EDITOR]: 'עריכת חללים',
  [STEPS.NEW_SPACES]: 'חללים חדשים שזוהו',
  [STEPS.MISSING_SPACES]: 'חללים קיימים שלא נמצאו',
  [STEPS.SIZE_CHANGES]: 'שינויים בגודל',
  [STEPS.WINDOWS]: 'חלונות שזוהו',
  [STEPS.CONFIRM]: 'סיכום ואישור',
};

// Window type labels in Hebrew
const WINDOW_TYPE_LABELS = {
  standard: 'חלון רגיל',
  french: 'חלון צרפתי',
  sliding: 'חלון הזזה',
  corner: 'חלון פינתי',
};

/**
 * FloorPlanAnalysisWizard Component
 */
function FloorPlanAnalysisWizard({
  floorId,
  floorName,
  projectId,
  onClose,
  onComplete,
  existingFilePath,
  existingFileUrl,
}) {
  // Determine if we're re-analyzing an existing file
  const isReanalysis = Boolean(existingFilePath);

  // State
  const [step, setStep] = useState(isReanalysis ? STEPS.ANALYZING : STEPS.UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(existingFileUrl || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [reanalysisStarted, setReanalysisStarted] = useState(false);

  // Selections for each step
  const [acceptedNewSpaces, setAcceptedNewSpaces] = useState({});
  const [keepMissingSpaces, setKeepMissingSpaces] = useState({});
  const [acceptSizeChanges, setAcceptSizeChanges] = useState({});
  const [acceptedWindows, setAcceptedWindows] = useState({});

  // Visual editor state
  const [floorPlanImageUrl, setFloorPlanImageUrl] = useState(null);
  const editorRef = useRef(null);

  // Master spaces for editor type dropdown
  const { data: masterSpaces } = useData(TABLES.MASTER_SPACES, {
    select: 'id, name, space_category',
  });

  // Desktop detection for visual editor
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width:768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width:768px)');
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const fileInputRef = useRef(null);

  // Hook
  const {
    isProcessing,
    uploadProgress,
    analysisResult,
    error,
    analyzeFloorPlan,
    reanalyzeFromPath,
    mergeSpacesFromAnalysis,
    getFloorPlanUrl,
    reset,
  } = useFloorPlanAnalysis({ floorId, projectId });

  // Derived data from analysis result
  const { newSpaces, missingSpaces, sizeChanges, detectedWindows } = useMemo(() => {
    if (!analysisResult?.comparison) {
      return { newSpaces: [], missingSpaces: [], sizeChanges: [], detectedWindows: [] };
    }
    return {
      newSpaces: analysisResult.comparison.newSpaces || [],
      missingSpaces: analysisResult.comparison.missingSpaces || [],
      sizeChanges: analysisResult.comparison.sizeChanges || [],
      detectedWindows: analysisResult.windows || [],
    };
  }, [analysisResult]);

  // Auto-start re-analysis when wizard opens with existing file
  useEffect(() => {
    if (isReanalysis && !reanalysisStarted && existingFilePath) {
      setReanalysisStarted(true);

      const startReanalysis = async () => {
        try {
          await reanalyzeFromPath(existingFilePath);
          // Move to first review step after analysis completes
          // The actual step will be determined by the next useEffect when analysisResult is set
        } catch (err) {
          // Error is handled by hook, go back to upload step to show error
          setStep(STEPS.UPLOAD);
        }
      };

      startReanalysis();
    }
  }, [isReanalysis, reanalysisStarted, existingFilePath, reanalyzeFromPath]);

  // Check if visual editor can be used
  const useVisualEditor = useMemo(() => {
    if (isMobile) return false;
    return analysisResult?.spaces?.some(s => s.bounding_box);
  }, [isMobile, analysisResult]);

  // Load floor plan image URL when analysis completes
  // For PDFs, render first page to image via pdfjs-dist
  useEffect(() => {
    if (!analysisResult?.filePath || floorPlanImageUrl) return;

    let cancelled = false;

    async function loadImage() {
      const url = await getFloorPlanUrl(analysisResult.filePath);
      if (!url || cancelled) return;

      const isPdf = analysisResult.filePath.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        try {
          const dataUrl = await pdfToImageUrl(url);
          if (!cancelled) setFloorPlanImageUrl(dataUrl);
        } catch (err) {
          console.error('PDF render failed, using signed URL:', err);
          if (!cancelled) setFloorPlanImageUrl(url);
        }
      } else {
        if (!cancelled) setFloorPlanImageUrl(url);
      }
    }

    loadImage();
    return () => {
      cancelled = true;
    };
  }, [analysisResult, floorPlanImageUrl, getFloorPlanUrl]);

  // Initialize selections and move to review step when analysis completes
  useEffect(() => {
    if (analysisResult) {
      // Default: accept all new spaces with high confidence
      const initialNew = {};
      newSpaces.forEach((space, idx) => {
        initialNew[idx] = space.confidence >= 0.7;
      });
      setAcceptedNewSpaces(initialNew);

      // Default: keep all existing spaces
      const initialKeep = {};
      missingSpaces.forEach((space, idx) => {
        initialKeep[idx] = true;
      });
      setKeepMissingSpaces(initialKeep);

      // Default: accept size changes for high confidence matches
      const initialSize = {};
      sizeChanges.forEach((change, idx) => {
        initialSize[idx] = false; // Default to keeping old size
      });
      setAcceptSizeChanges(initialSize);

      // Default: accept all detected windows with high confidence
      const initialWindows = {};
      detectedWindows.forEach((win, idx) => {
        initialWindows[idx] = win.confidence >= 0.7;
      });
      setAcceptedWindows(initialWindows);

      // Move to first review step (from ANALYZING step)
      if (step === STEPS.ANALYZING) {
        if (useVisualEditor) {
          setStep(STEPS.EDITOR);
        } else {
          const nextStep =
            newSpaces.length > 0
              ? STEPS.NEW_SPACES
              : missingSpaces.length > 0
                ? STEPS.MISSING_SPACES
                : sizeChanges.length > 0
                  ? STEPS.SIZE_CHANGES
                  : detectedWindows.length > 0
                    ? STEPS.WINDOWS
                    : STEPS.CONFIRM;
          setStep(nextStep);
        }
      }
    }
  }, [
    analysisResult,
    newSpaces,
    missingSpaces,
    sizeChanges,
    detectedWindows,
    step,
    useVisualEditor,
  ]);

  // Determine which steps to show based on analysis
  const activeSteps = useMemo(() => {
    const steps = [STEPS.UPLOAD, STEPS.ANALYZING];
    if (useVisualEditor) {
      // Visual editor replaces card-based space review steps
      steps.push(STEPS.EDITOR);
    } else {
      if (newSpaces.length > 0) steps.push(STEPS.NEW_SPACES);
      if (missingSpaces.length > 0) steps.push(STEPS.MISSING_SPACES);
      if (sizeChanges.length > 0) steps.push(STEPS.SIZE_CHANGES);
    }
    if (detectedWindows.length > 0) steps.push(STEPS.WINDOWS);
    steps.push(STEPS.CONFIRM);
    return steps;
  }, [newSpaces, missingSpaces, sizeChanges, detectedWindows, useVisualEditor]);

  // Get next step
  const getNextStep = useCallback(() => {
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex < activeSteps.length - 1) {
      return activeSteps[currentIndex + 1];
    }
    return null;
  }, [step, activeSteps]);

  // Get previous step
  const getPrevStep = useCallback(() => {
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex > 0) {
      return activeSteps[currentIndex - 1];
    }
    return null;
  }, [step, activeSteps]);

  // File handling
  const handleFileSelect = useCallback(file => {
    if (!file) return;

    setSelectedFile(file);

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragOver(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    e => {
      const file = e.target.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Start analysis — step transition is handled by the useEffect that watches analysisResult
  const handleStartAnalysis = useCallback(async () => {
    if (!selectedFile) return;

    setStep(STEPS.ANALYZING);

    try {
      await analyzeFloorPlan(selectedFile);
      // Step transition happens in the useEffect above (line ~230) once analysisResult updates,
      // which correctly checks useVisualEditor and routes to EDITOR or card-based review.
    } catch (err) {
      // Error is handled by hook, return to upload step
      setStep(STEPS.UPLOAD);
    }
  }, [selectedFile, analyzeFloorPlan]);

  // Navigation
  // Sync window names from editor before moving to windows step
  const syncWindowNamesFromEditor = useCallback(() => {
    if (!useVisualEditor || !editorRef.current) return;
    const shapes = editorRef.current.getShapes();
    if (!shapes?.length || !analysisResult?.spaces) return;

    // Build mapping: original name → editor name
    const nameMap = {};
    for (const shape of shapes) {
      const d = shape.spaceData;
      if (!d) continue;
      // Find original space by temp_id or index match
      const original = analysisResult.spaces.find(
        s => s.temp_id === d.temp_id || s.suggested_name_he === d._originalNameHe,
      );
      if (original && d.suggested_name_he !== original.suggested_name_he) {
        nameMap[original.suggested_name_he] = d.suggested_name_he;
        nameMap[original.suggested_name] = d.suggested_name_he;
      }
    }

    // Update window references
    if (Object.keys(nameMap).length > 0) {
      detectedWindows.forEach(w => {
        if (nameMap[w.space_name_he]) w.space_name_he = nameMap[w.space_name_he];
        if (nameMap[w.space_name]) w.space_name = nameMap[w.space_name];
      });
    }
  }, [useVisualEditor, analysisResult, detectedWindows]);

  const handleNext = useCallback(() => {
    const nextStep = getNextStep();
    if (nextStep) {
      // Sync window names when leaving editor step
      if (step === STEPS.EDITOR && nextStep === STEPS.WINDOWS) {
        syncWindowNamesFromEditor();
      }
      setStep(nextStep);
    }
  }, [getNextStep, step, syncWindowNamesFromEditor]);

  const handleBack = useCallback(() => {
    const prevStep = getPrevStep();
    if (prevStep && prevStep !== STEPS.ANALYZING) {
      setStep(prevStep);
    }
  }, [getPrevStep]);

  // Toggle space selection
  const toggleNewSpace = useCallback(index => {
    setAcceptedNewSpaces(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const toggleMissingSpace = useCallback(index => {
    setKeepMissingSpaces(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const toggleSizeChange = useCallback(index => {
    setAcceptSizeChanges(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const toggleWindow = useCallback(index => {
    setAcceptedWindows(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  // Calculate summary
  const summary = useMemo(() => {
    const spacesToCreate = newSpaces
      .filter((_, idx) => acceptedNewSpaces[idx])
      .map(space => ({
        ...space,
        action: 'create',
      }));

    const spacesToRemove = missingSpaces
      .filter((_, idx) => !keepMissingSpaces[idx])
      .map(space => ({
        ...space,
        action: 'remove',
      }));

    const spacesToUpdate = sizeChanges
      .filter((_, idx) => acceptSizeChanges[idx])
      .map(change => ({
        ...change,
        action: 'update',
      }));

    const windowsToCreate = detectedWindows
      .filter((_, idx) => acceptedWindows[idx])
      .map(window => ({
        ...window,
        action: 'create',
      }));

    return {
      spacesToCreate,
      spacesToRemove,
      spacesToUpdate,
      windowsToCreate,
      totalChanges:
        spacesToCreate.length +
        spacesToRemove.length +
        spacesToUpdate.length +
        windowsToCreate.length,
    };
  }, [
    newSpaces,
    missingSpaces,
    sizeChanges,
    detectedWindows,
    acceptedNewSpaces,
    keepMissingSpaces,
    acceptSizeChanges,
    acceptedWindows,
  ]);

  // Build spaces data from editor shapes
  const getEditorSpacesData = useCallback(() => {
    if (!editorRef.current) return { spacesToCreate: [], spacesToUpdate: [], spaceIdsToDelete: [] };

    const shapes = editorRef.current.getShapes();
    const spacesToCreate = [];
    const spacesToUpdate = [];

    for (const shape of shapes) {
      const d = shape.spaceData;
      if (!d || !d.included) continue;

      if (d.status === 'matched') {
        // Existing space — might have updated size from editor
        if (d.existingId) {
          spacesToUpdate.push({
            id: d.existingId,
            room_size: d.estimated_size_sqm,
            room_length: d.room_length || null,
            room_width: d.room_width || null,
          });
        }
      } else {
        // New space or size_change treated as new
        spacesToCreate.push({
          suggested_name: d.suggested_name || '',
          suggested_name_he: d.suggested_name_he || '',
          custom_space_name: d.suggested_name_he || d.suggested_name || '',
          master_space_id: d.master_space_id,
          estimated_size_sqm: d.estimated_size_sqm,
          room_size: d.estimated_size_sqm,
          room_length: d.room_length || null,
          room_width: d.room_width || null,
          confidence: d.confidence,
          suggested_type: d.suggested_type || 'Other',
        });
      }
    }

    // Missing spaces (not on canvas) — keep all by default in editor mode
    const spaceIdsToDelete = missingSpaces
      .filter((_, idx) => !keepMissingSpaces[idx])
      .map(space => space.id);

    return { spacesToCreate, spacesToUpdate, spaceIdsToDelete };
  }, [missingSpaces, keepMissingSpaces]);

  // Submit changes
  const handleSubmit = useCallback(async () => {
    if (!analysisResult?.analysisId) return;

    try {
      let spacesToCreate;
      let spacesToUpdate;
      let spaceIdsToDelete;

      if (useVisualEditor) {
        // Get data from editor shapes
        const editorData = getEditorSpacesData();
        spacesToCreate = editorData.spacesToCreate;
        spacesToUpdate = editorData.spacesToUpdate;
        spaceIdsToDelete = editorData.spaceIdsToDelete;
      } else {
        // Card-based flow
        spacesToCreate = newSpaces
          .filter((_, idx) => acceptedNewSpaces[idx])
          .map(space => ({
            suggested_name: space.suggested_name,
            suggested_name_he: space.suggested_name_he,
            custom_space_name: space.suggested_name_he || space.suggested_name,
            master_space_id: space.master_space_id,
            estimated_size_sqm: space.estimated_size_sqm,
            room_size: space.estimated_size_sqm,
            room_length: space.room_length,
            room_width: space.room_width,
            confidence: space.confidence,
            suggested_type: space.suggested_type,
          }));

        spacesToUpdate = sizeChanges
          .filter((_, idx) => acceptSizeChanges[idx])
          .map(change => ({
            id: change.existingId,
            room_size: change.detectedSize,
            room_length: change.detectedLength,
            room_width: change.detectedWidth,
          }));

        spaceIdsToDelete = missingSpaces
          .filter((_, idx) => !keepMissingSpaces[idx])
          .map(space => space.id);
      }

      // Windows — always from card-based selection
      const windowsToCreate = detectedWindows
        .filter((_, idx) => acceptedWindows[idx])
        .map(window => ({
          space_name: window.space_name,
          space_name_he: window.space_name_he,
          window_type: window.window_type,
          width_cm: window.width_cm,
          height_cm: window.height_cm,
          sill_height_cm: window.sill_height_cm,
          confidence: window.confidence,
        }));

      await mergeSpacesFromAnalysis(
        analysisResult.analysisId,
        spacesToCreate,
        spacesToUpdate,
        spaceIdsToDelete,
        windowsToCreate,
      );

      if (onComplete) {
        onComplete(summary);
      }
      onClose();
    } catch (err) {
      // Error handled by hook
    }
  }, [
    analysisResult,
    useVisualEditor,
    getEditorSpacesData,
    newSpaces,
    acceptedNewSpaces,
    sizeChanges,
    acceptSizeChanges,
    missingSpaces,
    keepMissingSpaces,
    detectedWindows,
    acceptedWindows,
    mergeSpacesFromAnalysis,
    onComplete,
    onClose,
    summary,
  ]);

  // Handle close
  const handleClose = useCallback(() => {
    // Cleanup preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    reset();
    onClose();
  }, [previewUrl, reset, onClose]);

  // Can go back?
  const canGoBack = step !== STEPS.UPLOAD && step !== STEPS.ANALYZING && step !== STEPS.EDITOR;

  // Get button text
  const getButtonText = () => {
    if (step === STEPS.UPLOAD) return 'נתח תוכנית';
    if (step === STEPS.CONFIRM) {
      return summary.totalChanges === 0 ? 'סגור' : 'אישור וביצוע';
    }
    return 'הבא';
  };

  const isEditorStep = step === STEPS.EDITOR;
  const modalClass = isEditorStep
    ? `${modalStyles.modalFullScreen}`
    : `${modalStyles.modal} ${modalStyles.modalLarge}`;

  return (
    <div className={modalClass} dir="rtl" onClick={e => e.stopPropagation()}>
      {/* Header — hidden during editor step (toolbar replaces it) */}
      {!isEditorStep && (
        <div className={modalStyles.header}>
          <div className={styles.headerTop}>
            <h2 className={modalStyles.headerTitle}>{STEP_TITLES[step]}</h2>
            <button className={styles.closeButton} onClick={handleClose}>
              <X size={20} />
            </button>
          </div>
          {floorName && <div className={modalStyles.headerSubtitle}>קומה: {floorName}</div>}
          {step !== STEPS.UPLOAD && step !== STEPS.ANALYZING && (
            <div className={styles.stepIndicator}>
              {activeSteps
                .filter(s => s !== STEPS.UPLOAD && s !== STEPS.ANALYZING)
                .map(s => (
                  <div
                    key={s}
                    className={`${styles.stepDot} ${s === step ? styles.active : ''} ${activeSteps.indexOf(s) < activeSteps.indexOf(step) ? styles.completed : ''}`}
                  />
                ))}
            </div>
          )}
        </div>
      )}

      {/* Editor step — full-screen, no content wrapper */}
      {isEditorStep && (
        <FloorPlanEditor
          imageUrl={floorPlanImageUrl}
          spaces={analysisResult?.spaces || []}
          metadata={analysisResult?.metadata}
          masterSpaces={masterSpaces}
          onSpacesChanged={() => {}}
          onEditorReady={editor => {
            editorRef.current = editor;
          }}
        />
      )}

      {/* Content — for non-editor steps */}
      {!isEditorStep && (
        <div className={modalStyles.content}>
          {error && (
            <div className={modalStyles.error}>
              <Warning size={18} />
              {error}
            </div>
          )}

          {/* Step: Upload */}
          {step === STEPS.UPLOAD && (
            <div className={styles.uploadSection}>
              <div
                className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ''} ${selectedFile ? styles.hasFile : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleBrowseClick}
              >
                {selectedFile ? (
                  <div className={styles.filePreview}>
                    {previewUrl ? (
                      <img src={previewUrl} alt="Preview" className={styles.previewImage} />
                    ) : (
                      <FileImage size={48} className={styles.fileIcon} />
                    )}
                    <div className={styles.fileName}>{selectedFile.name}</div>
                    <button
                      className={styles.removeFile}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={48} className={styles.uploadIcon} />
                    <div className={styles.dropText}>גרור קובץ לכאן או לחץ לבחירה</div>
                    <div className={styles.dropHint}>PDF, PNG, JPG עד 50MB</div>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* Step: Analyzing */}
          {step === STEPS.ANALYZING && (
            <div className={styles.analyzingSection}>
              <div className={styles.spinnerContainer}>
                <SpinnerGap size={48} className={styles.spinner} />
              </div>
              <div className={styles.progressText}>
                {uploadProgress < 50 ? 'מעלה קובץ...' : 'מנתח את התוכנית...'}
              </div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className={styles.progressPercent}>{uploadProgress}%</div>
            </div>
          )}

          {/* Step: New Spaces */}
          {step === STEPS.NEW_SPACES && (
            <div className={styles.spacesSection}>
              <div className={styles.sectionDescription}>
                הבינה המלאכותית זיהתה את החללים הבאים בתוכנית. בחר אילו להוסיף:
              </div>
              <div className={styles.spacesList}>
                {newSpaces.map((space, idx) => (
                  <div
                    key={idx}
                    className={`${styles.spaceCard} ${acceptedNewSpaces[idx] ? styles.selected : ''}`}
                    onClick={() => toggleNewSpace(idx)}
                  >
                    <div className={styles.spaceCheckbox}>
                      {acceptedNewSpaces[idx] ? <Check size={18} weight="bold" /> : null}
                    </div>
                    <div className={styles.spaceInfo}>
                      <div className={styles.spaceName}>
                        {space.suggested_name_he || space.suggested_name}
                      </div>
                      <div className={styles.spaceDetails}>
                        {space.suggested_type} • {space.estimated_size_sqm?.toFixed(1)} מ&quot;ר
                      </div>
                    </div>
                    <div
                      className={`${styles.confidenceBadge} ${space.confidence >= 0.8 ? styles.high : space.confidence >= 0.6 ? styles.medium : styles.low}`}
                    >
                      {Math.round(space.confidence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: Missing Spaces */}
          {step === STEPS.MISSING_SPACES && (
            <div className={styles.spacesSection}>
              <div className={styles.sectionDescription}>
                החללים הבאים קיימים בפרויקט אך לא נמצאו בתוכנית. בחר אילו לשמור:
              </div>
              <div className={styles.spacesList}>
                {missingSpaces.map((space, idx) => (
                  <div
                    key={idx}
                    className={`${styles.spaceCard} ${keepMissingSpaces[idx] ? styles.selected : styles.removed}`}
                    onClick={() => toggleMissingSpace(idx)}
                  >
                    <div className={styles.spaceCheckbox}>
                      {keepMissingSpaces[idx] ? (
                        <Check size={18} weight="bold" />
                      ) : (
                        <Trash size={18} />
                      )}
                    </div>
                    <div className={styles.spaceInfo}>
                      <div className={styles.spaceName}>{space.custom_space_name || 'ללא שם'}</div>
                      <div className={styles.spaceDetails}>
                        {space.room_size?.toFixed(1)} מ&quot;ר
                      </div>
                    </div>
                    <div className={styles.actionBadge}>
                      {keepMissingSpaces[idx] ? 'שמור' : 'הסר'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: Size Changes */}
          {step === STEPS.SIZE_CHANGES && (
            <div className={styles.spacesSection}>
              <div className={styles.sectionDescription}>
                זוהו הבדלים בגודל עבור החללים הבאים. בחר האם לעדכן:
              </div>
              <div className={styles.spacesList}>
                {sizeChanges.map((change, idx) => (
                  <div
                    key={idx}
                    className={`${styles.spaceCard} ${acceptSizeChanges[idx] ? styles.selected : ''}`}
                    onClick={() => toggleSizeChange(idx)}
                  >
                    <div className={styles.spaceCheckbox}>
                      {acceptSizeChanges[idx] ? <Check size={18} weight="bold" /> : null}
                    </div>
                    <div className={styles.spaceInfo}>
                      <div className={styles.spaceName}>{change.existingName}</div>
                      <div className={styles.sizeChange}>
                        <span className={styles.oldSize}>
                          {change.existingSize?.toFixed(1)} מ&quot;ר
                        </span>
                        <ArrowsLeftRight size={16} />
                        <span className={styles.newSize}>
                          {change.detectedSize?.toFixed(1)} מ&quot;ר
                        </span>
                        <span
                          className={`${styles.sizeDiff} ${change.sizeDifference > 0 ? styles.increase : styles.decrease}`}
                        >
                          ({change.sizeDifference > 0 ? '+' : ''}
                          {change.sizeDifference?.toFixed(1)})
                        </span>
                      </div>
                    </div>
                    <div className={styles.actionBadge}>
                      {acceptSizeChanges[idx] ? 'עדכן' : 'שמור ישן'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: Windows */}
          {step === STEPS.WINDOWS && (
            <div className={styles.spacesSection}>
              <div className={styles.sectionDescription}>
                זוהו חלונות בתוכנית. בחר אילו להוסיף לפרויקט:
              </div>
              <div className={styles.spacesList}>
                {detectedWindows.map((window, idx) => (
                  <div
                    key={idx}
                    className={`${styles.spaceCard} ${acceptedWindows[idx] ? styles.selected : ''}`}
                    onClick={() => toggleWindow(idx)}
                  >
                    <div className={styles.spaceCheckbox}>
                      {acceptedWindows[idx] ? <Check size={18} weight="bold" /> : null}
                    </div>
                    <div className={styles.spaceInfo}>
                      <div className={styles.spaceName}>
                        <Square size={16} style={{ marginLeft: 6 }} />
                        {window.space_name_he || window.space_name}
                      </div>
                      <div className={styles.spaceDetails}>
                        {WINDOW_TYPE_LABELS[window.window_type] || window.window_type} •{' '}
                        {window.width_cm}×{window.height_cm} ס&quot;מ
                        {window.sill_height_cm && ` • גובה אדן ${window.sill_height_cm} ס"מ`}
                      </div>
                    </div>
                    <div
                      className={`${styles.confidenceBadge} ${window.confidence >= 0.8 ? styles.high : window.confidence >= 0.6 ? styles.medium : styles.low}`}
                    >
                      {Math.round(window.confidence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: Confirm */}
          {step === STEPS.CONFIRM && (
            <div className={styles.confirmSection}>
              {summary.totalChanges === 0 ? (
                <div className={styles.noChanges}>
                  <Check size={48} className={styles.noChangesIcon} />
                  <div className={styles.noChangesText}>
                    {newSpaces.length === 0 &&
                    missingSpaces.length === 0 &&
                    sizeChanges.length === 0 &&
                    detectedWindows.length === 0
                      ? 'הניתוח הושלם - לא נמצאו הבדלים'
                      : 'לא נבחרו שינויים לביצוע'}
                  </div>
                  <div className={styles.noChangesSubtext}>
                    {newSpaces.length === 0 &&
                    missingSpaces.length === 0 &&
                    sizeChanges.length === 0 &&
                    detectedWindows.length === 0
                      ? 'החללים בפרויקט תואמים לתוכנית'
                      : 'ניתן לחזור ולבחור שינויים או לסגור את החלון'}
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.summaryTitle}>סיכום השינויים:</div>

                  {summary.spacesToCreate.length > 0 && (
                    <div className={styles.summaryGroup}>
                      <div className={styles.summaryGroupTitle}>
                        <Plus size={18} />
                        {summary.spacesToCreate.length} חללים חדשים
                      </div>
                      <div className={styles.summaryList}>
                        {summary.spacesToCreate.map((space, idx) => (
                          <div key={idx} className={styles.summaryItem}>
                            {space.suggested_name_he || space.suggested_name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {summary.spacesToRemove.length > 0 && (
                    <div className={styles.summaryGroup}>
                      <div className={`${styles.summaryGroupTitle} ${styles.remove}`}>
                        <Trash size={18} />
                        {summary.spacesToRemove.length} חללים להסרה
                      </div>
                      <div className={styles.summaryList}>
                        {summary.spacesToRemove.map((space, idx) => (
                          <div key={idx} className={styles.summaryItem}>
                            {space.custom_space_name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {summary.spacesToUpdate.length > 0 && (
                    <div className={styles.summaryGroup}>
                      <div className={`${styles.summaryGroupTitle} ${styles.update}`}>
                        <ArrowsLeftRight size={18} />
                        {summary.spacesToUpdate.length} עדכוני גודל
                      </div>
                      <div className={styles.summaryList}>
                        {summary.spacesToUpdate.map((change, idx) => (
                          <div key={idx} className={styles.summaryItem}>
                            {change.existingName}: {change.existingSize} → {change.detectedSize}{' '}
                            מ&quot;ר
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {summary.windowsToCreate.length > 0 && (
                    <div className={styles.summaryGroup}>
                      <div className={`${styles.summaryGroupTitle} ${styles.windows}`}>
                        <Square size={18} />
                        {summary.windowsToCreate.length} חלונות חדשים
                      </div>
                      <div className={styles.summaryList}>
                        {summary.windowsToCreate.map((window, idx) => (
                          <div key={idx} className={styles.summaryItem}>
                            {window.space_name_he || window.space_name} -{' '}
                            {WINDOW_TYPE_LABELS[window.window_type] || window.window_type}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className={modalStyles.footer}>
        {canGoBack ? (
          <button className={modalStyles.secondaryButton} onClick={handleBack}>
            חזור
          </button>
        ) : (
          <button className={modalStyles.cancelButton} onClick={handleClose}>
            ביטול
          </button>
        )}

        {step === STEPS.UPLOAD && (
          <button
            className={modalStyles.primaryButton}
            onClick={handleStartAnalysis}
            disabled={!selectedFile}
          >
            {getButtonText()}
            <ArrowLeft size={18} />
          </button>
        )}

        {step !== STEPS.UPLOAD && step !== STEPS.ANALYZING && (
          <button
            className={`${modalStyles.primaryButton} ${isProcessing ? 'loading' : ''}`}
            onClick={
              step === STEPS.CONFIRM
                ? summary.totalChanges === 0
                  ? handleClose
                  : handleSubmit
                : handleNext
            }
            disabled={isProcessing}
          >
            {getButtonText()}
            {!isProcessing && summary.totalChanges > 0 && <ArrowLeft size={18} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default React.memo(FloorPlanAnalysisWizard);
